import { TemplatePortal } from '@angular/cdk/portal';
import { AsyncPipe } from '@angular/common';
import {
  afterNextRender,
  Component,
  inject,
  OnDestroy,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TranslatePipe } from '@cmi/addin-core';
import { combineLatest, map, Observable, switchMap } from 'rxjs';
import * as wordControls from '../../../../lib/word-content-controls';
import { App } from '../../../app';
import { PropertyFormatForm } from '../../../property-format/property-format-form';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';
import { ProviderTypeIcon } from '../../../shared/provider-type-icon/provider-type-icon';
import { SearchBar } from '../../../shared/search-bar/search-bar';
import { ContentProvider, ContentProviderProperty } from '../../home.models';
import { HomeService } from '../../home.service';

@Component({
  selector: 'cmi-provider-properties',
  imports: [
    AsyncPipe,
    ProviderTypeIcon,
    Breadcrumb,
    TranslatePipe,
    SearchBar,
    RouterModule,
    PropertyFormatForm
  ],
  templateUrl: './provider-properties.html'
})
export class ProviderProperties implements OnDestroy {
  private readonly _route = inject(ActivatedRoute);
  private readonly _homeService = inject(HomeService);
  private readonly _viewContainerRef = inject(ViewContainerRef);
  private readonly _appComponent = inject(App);
  private readonly _footerPortal = viewChild.required<TemplateRef<unknown>>('footerPortal');

  private readonly _searchTerm = signal<string>('');
  public readonly expandedPropertyKey = signal<string | null>(null);

  public readonly tag$: Observable<string | null> = this._route.paramMap.pipe(
    map((params) => params.get('tag'))
  );
  public readonly key$: Observable<string | null> = this._route.paramMap.pipe(
    map((params) => params.get('key'))
  );

  public readonly provider$: Observable<ContentProvider | null> = combineLatest([
    this.tag$,
    this.key$
  ]).pipe(switchMap(([tag, key]) => this._homeService.getContentProvider(tag ?? '', key ?? '')));

  private readonly _allProperties$: Observable<ContentProviderProperty[]> = this.provider$.pipe(
    map((provider) => (provider ? provider.properties : []))
  );

  public readonly properties$: Observable<ContentProviderProperty[]> = combineLatest([
    this._allProperties$,
    toObservable(this._searchTerm)
  ]).pipe(
    map(([properties, searchTerm]) => {
      if (!searchTerm.trim()) {
        return properties;
      }
      const lowerSearchTerm = searchTerm.toLowerCase();
      return properties.filter(
        (property) =>
          property.key.toLowerCase().includes(lowerSearchTerm) ||
          property.name.toLowerCase().includes(lowerSearchTerm)
      );
    })
  );

  public async addProperty(property: ContentProviderProperty, providerName: string): Promise<void> {
    try {
      // No tag set here — the tag is used by PropertyFormat to identify the bound property,
      // which is not needed for a simple (unformatted) insert.
      await wordControls.insertRichTextControl({
        title: `CMI_${providerName}.${property.key}`,
        placeholder: `${property.key}`
      });
    } catch (error) {
      console.error('Error inserting content control:', error);
    }
  }

  public toggleFormattedInsert(propertyKey: string): void {
    const currentExpanded = this.expandedPropertyKey();
    if (currentExpanded === propertyKey) {
      this.expandedPropertyKey.set(null);
    } else {
      this.expandedPropertyKey.set(propertyKey);
    }
  }

  public isPropertyExpanded(propertyKey: string): boolean {
    return this.expandedPropertyKey() === propertyKey;
  }

  public supportsFormatting(typeName: string): boolean {
    return typeName === 'DateTime' || typeName === 'Boolean' || typeName === 'Int32';
  }

  public async onApplyFormattedProperty(
    property: ContentProviderProperty,
    providerKey: string,
    formatValue: string
  ): Promise<void> {
    await wordControls.insertRichTextControl({
      title: `CMI_${providerKey}.${property.key}:${formatValue}`,
      tag: { extra: [property.key] },
      placeholder: `${property.name}`
    });

    this.expandedPropertyKey.set(null);
  }

  public constructor() {
    afterNextRender(() => {
      const portal = new TemplatePortal(this._footerPortal(), this._viewContainerRef);
      this._appComponent.footerPortal.set(portal);
    });
  }

  public ngOnDestroy(): void {
    this._appComponent.footerPortal.set(null);
  }

  public onSearchTermChanged(term: string): void {
    this._searchTerm.set(term);
  }
}
